import React, { useEffect, useState } from "react";
import { DropdownComponent, InputComponent } from "../../core/Forms";
import LocationSelector from "../../common/Address";
import { businessInfo } from "../../../@types/onboarding";
import { INDUSTRIES } from "../../../constants/industries";
import {
  clearFieldError,
  handleError,
  validatePhoneNumber,
} from "../../../utils";
import { useUserStore } from "../../../state";
import { validateInfoForm } from "../../../modules/auth/register";
import { DEBOUNCE_DELAY } from "../../../constants/todo";
import { verifyCode } from "../../../utils/verifyCode";
import PhoneInputComponent from "../../common/PhoneInputComponent";
import { useCompanyStore } from "../../../state/useCompanyStore";
import RegistrationCodeInput from "../../core/Forms/RegistrationCodeInput";
import DownRightArrow from "../../../assets/icons/DownRightArrow";
import { CloseIcon } from "../../../assets/icons";
import RegistrationCodeSection from "../../core/Forms/RegistrationCodeInput/RegistrationCodeSection";

interface ValidationResults {
  firstName: string;
  lastName: string;
  businessName: string;
  industry: string;
  code: string;
  address: string;
  state: string;
  city: string;
  phone: string;
  [key: string]: string;
}

const initialFormData = {
  firstName: "",
  lastName: "",
  industry: "",
  businessName: "",
  code: "",
  address: "",
  state: "",
  city: "",
  phone: "",
};

interface Option {
  code: string;
  label: string;
}

interface BusinessInfoModalProps {
  setShowBusinessInfo: (ShowBusinessInfo: boolean) => void;
}

const BusinessInfoModal = ({ setShowBusinessInfo }: BusinessInfoModalProps) => {
  const { company } = useCompanyStore();
  const { fetchCompanyProfile } = useCompanyStore();
  const initialPhoneNumber = "+234";
  const dropDownArray = INDUSTRIES;
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [selectedValue, setSelectedValue] = useState("");
  const [state, setState] = React.useState("");
  const [city, setCity] = React.useState("");
  const [rcPrefix, setRcPrefix] = useState("");
  const [rcType, setRcType] = useState("");
  const [registrationCode, setRegistrationCode] = useState("");
  const [isSelected, setIsSelected] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState<string | null>(
    null,
  );
  const [error, setError] = React.useState<string>();
  const [formData, setFormData] = useState<businessInfo>(initialFormData);
  const [validationResults, setValidationResults] =
    useState<ValidationResults>(initialFormData);
  const updateProfile = useCompanyStore((state) => state.updateCompanyProfile);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === "code") {
      setRegistrationCode(value);
    }
    clearFieldError(name, validationResults, setValidationResults);
  };

  const handleDropDownChange = (name: string, selectedValue: string) => {
    setFormData({ ...formData, industry: selectedValue });
    clearFieldError(name, validationResults, setValidationResults);
  };

  const handleLocationChange = (selectedState: string, cities: string[]) => {
    setState(selectedState);
    setCity(cities?.length > 0 ? cities[0] : "");
    const updatedFormData = {
      ...formData,
      state: selectedState,
      city: cities?.length > 0 ? cities[0] : "",
    };
    setFormData(updatedFormData);
  };

  const handlePhoneNumberChange = (value: string) => {
    if (value) {
      const trimmedValue = value.trim();
      const normalizedValue = trimmedValue.replace(/\D/g, "");

      const isValidPhoneNumber = validatePhoneNumber(normalizedValue);

      if (isValidPhoneNumber) {
        setFormData({ ...formData, phone: "+" + normalizedValue });
        setValidationResults({ ...validationResults, phone: "" });
      } else {
        setValidationResults({
          ...validationResults,
          phone: "Invalid phone number format",
        });
      }
    } else {
      setFormData({ ...formData, phone: "" });
      setValidationResults({ ...validationResults, phone: "" });
    }
  };

  const handlePhoneNumberKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.currentTarget.value?.length >= 17) {
      event.preventDefault();
    }
  };

  const handleSave = async () => {
    let hasErrors = false;
    let missingFields = [];
    const updatedValidationResults = { ...validationResults };

    Object.entries(formData).forEach(([key, value]) => {
      if (key !== "code") {
        if (value === null || value === undefined || value === "") {
          hasErrors = true;
          missingFields.push(key);
          if (key === "firstName") {
            updatedValidationResults[key] = "First Name is required";
          } else if (key === "lastName") {
            updatedValidationResults[key] = "Last Name is required";
          } else if (key === "businessName") {
            updatedValidationResults[key] = "Business Name is required";
          } else {
            updatedValidationResults[key] = `${key} is required`;
          }
        }
      }
    });

    if (hasErrors) {
      setIsSubmitted(true);
      setValidationResults(updatedValidationResults);
    } else {
      setIsLoading(true);
      try {
        await updateProfile({
          firstName: formData.firstName,
          lastName: formData.lastName,
          industry: formData.industry,
          address: formData.address,
          state: formData.state,
          city: formData.city,
          code: formData.code || "",
          phone: formData.phone,
          businessName: formData.businessName,
        });
        fetchCompanyProfile();
        setShowBusinessInfo(false);
        setIsLoading(false);
      } catch (error) {
        setShowBusinessInfo(false);
        setError("Profile update failed. Try again");
        handleError("Profile update failed!");
      }
    }
  };

  const validCodeLength = [8, 9, 10];
  useEffect(() => {
    const handleRcVerification = async () => {
      if (validCodeLength.includes(registrationCode.length)) {
        setVerificationMessage("Verifying registration code...");
        const result = await verifyCode({
          rcnumber: registrationCode,
          businessType: rcType,
        });
        setVerificationMessage(result);
      } else {
        setVerificationMessage(null);
      }
    };

    if (validCodeLength.includes(registrationCode.length)) {
      const handler = setTimeout(handleRcVerification, DEBOUNCE_DELAY);
      return () => clearTimeout(handler);
    } else {
      setVerificationMessage(null);
    }
  }, [registrationCode, rcType]);

  useEffect(() => {
    if (company) {
      setFormData({
        ...formData,
        firstName: company.firstName,
        lastName: company.lastName,
        businessName: company.businessName,
        city: company?.city ?? "",
        state: company?.state ?? "",
        address: company.address,
        code: company?.code ?? "",
        phone: company.phone,
        industry: company?.industry ?? "",
      });
    }
  }, [company]);

  const renderErrorMessage = () => {
    if (error) {
      return <div className="text-red-500 mt-2">{error}</div>;
    }
    return null;
  };

  const handleRegistrationCodeChange = (option: Option) => {
    setRcPrefix(option.code);
    setRcType(option.label);
    setRegistrationCode("");
    setError("");
  };

  const handleOptionSelect = () => {
    setIsSelected(true);
  };

  return (
    <div className="bg-gray-900 bg-opacity-30 fixed top-0 right-0 left-0 z-50 h-[100vh] flex items-center justify-center p-4 ">
      <div className="relative max-h-screen min-w-[340px] w-full sm:w-[450px] md:w-[570px] lg:w-[650px] p-4 md:p-6 bg-white sm:rounded-2xl overflow-y-auto pb-[60px]">
        <CloseIcon
          className="absolute md:top-6 top-4 md:right-6 right-4 w-[30px] flex justify-center items-center p-1 cursor-pointer"
          onClick={() => setShowBusinessInfo(false)}
        />
        <div className="text-[#212121]">
          <h3 className="text-[18px] md:text-[20px] leading-6 font-semibold">
            Business Information
          </h3>
          {error && renderErrorMessage()}
          <div className="space-y-5 mt-8">
            <div className="md:flex gap-5 w-full">
              <div className="mt-5 md:mt-0 w-full">
                <div className="flex gap-1 items-center text-[14px]">
                  <p className="text-[14px] leading-5 text-[#000000]">
                    First Name
                  </p>
                  <p className="leading-5 text-[#F04438]">*</p>
                </div>
                <InputComponent
                  placeholder="Peru"
                  name="firstName"
                  type="text"
                  handleChange={handleInputChange}
                  value={formData.firstName}
                  error={validationResults.firstName}
                  className="w-full h-10 md:h-12 placeholder:text-[16px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                />
              </div>
              <div className="mt-5 md:mt-0 w-full">
                <div className="flex gap-1 items-center text-[14px]">
                  <p className="text-[14px] leading-5 text-[#000000]">
                    Last Name
                  </p>
                  <p className="leading-5 text-[#F04438]">*</p>
                </div>
                <InputComponent
                  placeholder="Para"
                  name="lastName"
                  type="text"
                  handleChange={handleInputChange}
                  value={formData.lastName}
                  error={validationResults.lastName}
                  className="w-full h-10 md:h-12 placeholder:text-[16px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                />
              </div>
            </div>

            <div className="md:flex gap-5 w-full">
              <div className="w-full">
                <div className="flex gap-1 items-center text-[14px]">
                  <p className="text-[14px] leading-5 text-[#000000]">
                    Business Name
                  </p>
                  <p className="leading-5 text-[#F04438]">*</p>
                </div>
                <InputComponent
                  placeholder="Business Name"
                  name="businessName"
                  type="text"
                  handleChange={handleInputChange}
                  value={formData.businessName}
                  error={validationResults.businessName}
                  className="w-full h-10 md:h-12 placeholder:text-[16px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                />
              </div>

              <div className="mt-5 md:mt-0 w-full">
                <div className="flex gap-1 items-center text-[14px]">
                  <p className="text-[14px] leading-5 text-[#000000]">
                    Industry
                  </p>
                  <p className="leading-5 text-[#F04438]">*</p>
                </div>
                <DropdownComponent
                  options={dropDownArray}
                  name="city"
                  placeholder="Select an Industry"
                  error={validationResults.industry}
                  onChange={handleDropDownChange}
                  iconClassName="mt-[6px] md:mt-0"
                  dropdownClassName="z-10 max-h-52 overflow-y-auto bg-white border border-gray-300 left-0 right-0 top-full w-full placeholder:text-[16px] text-[16px]"
                  selectedValue={selectedValue || company.industry}
                  setSelectedValue={setSelectedValue}
                  isSubmitted={isSubmitted}
                  enableSearch={true}
                />
              </div>
            </div>

            <div className="md:flex gap-5 w-full">
              <div className="w-full">
                <div className="flex gap-1 items-center text-[14px]">
                  <p className="text-[14px] leading-5 text-[#000000]">
                    Phone number
                  </p>
                  <p className="leading-5 text-[#F04438]">*</p>
                </div>
                <PhoneInputComponent
                  // placeholder={placeholder}
                  initialPhoneNumber={initialPhoneNumber}
                  onPhoneNumberChange={handlePhoneNumberChange}
                  onKeyDown={handlePhoneNumberKeyDown}
                  value={formData.phone}
                  inputClass="onboarding"
                  containerClass="onboarding"
                  disableDropdown
                />
                <div className="relative w-full ">
                  {validationResults.phone &&
                    validationResults.phone?.length > 0 && (
                      <p className="absolute left-0 whitespace-nowrap text-red-500 text-[12px]">
                        {validationResults.phone}
                      </p>
                    )}
                </div>
              </div>
              <div className="mt-5 md:mt-0 w-full">
                <div className="flex gap-1 items-center text-[14px]">
                  <p className="text-[14px] leading-5 text-[#000000]">
                    Address
                  </p>
                  <p className="leading-5 text-[#F04438]">*</p>
                </div>
                <InputComponent
                  placeholder="Business Address"
                  name="address"
                  handleChange={handleInputChange}
                  value={formData.address}
                  error={validationResults.address}
                  type="text"
                  className="w-full h-10 md:h-12 placeholder:text-[16px] placeholder:text-[#98A2B3] rounded-lg border-[#D0D5DD]"
                />
              </div>
            </div>

            <div className="w-full">
              <LocationSelector
                display="flex flex-1 flex-col md:flex-row items-center w-full gap-5"
                onLocationChange={handleLocationChange}
                onError={(error: any) => {}}
                selectedState={(state || company.state) ?? ""}
                selectedCity={(city || company.city) ?? ""}
                stateError={validationResults.state}
                cityError={validationResults.city}
                isSubmitted={isSubmitted}
              />
            </div>
            <div className="w-full">
              <RegistrationCodeInput
                id="businessType"
                name="businessType"
                value={rcType}
                onChange={handleRegistrationCodeChange}
                onSelect={handleOptionSelect}
                error={error}
              />
              {isSelected && (
                <RegistrationCodeSection
                  id="businessType"
                  name="businessType"
                  value={registrationCode}
                  rcPrefix={rcPrefix}
                  onChange={(e) => {
                    setRegistrationCode(e.target.value);
                    handleInputChange(e);
                  }}
                  verificationResult={verificationMessage}
                />
              )}
            </div>
          </div>

          <div className="w-full gap-[5%] flex justify-between mt-8 md:mt-10 pb-[20px] md:pb-0">
            <button
              type="button"
              onClick={() => setShowBusinessInfo(false)}
              className={`text-[#044E97] w-full h-12 rounded-md font-semibold border border-gray-300 hover:bg-slate-100`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isLoading}
              className={`text-[#ffffff] bg-[#044E97] w-full h-12 rounded-md font-semibold`}
            >
              {isLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessInfoModal;
